import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useBluelagoon } from "@bluelagoon";
import { Button } from "@shadcn-in-nx/ui/button";
import { Textarea } from "@shadcn-in-nx/ui/textarea";
import { Loader2, Play, Trash2 } from "lucide-react";
import { toast } from "sonner";
import PlaygroundTracePanel from "./PlaygroundTracePanel";

export default function SupportPlayground() {
    const { t } = useTranslation();
    const [message, setMessage] = useState("");
    const [context, setContext] = useState("");
    const [result, setResult] = useState(null);
    const { call: testAgent, isLoading } = useBluelagoon({ method: "testSupportAgent" });

    const handleRun = useCallback(async () => {
        if (!message.trim()) return;
        setResult(null);
        const res = await testAgent({
            message: message.trim(),
            customContext: context.trim() || undefined,
        }).catch((e) => { toast.error(e?.response?.data?.error || "Agent test failed"); return null; });
        if (res?.data) setResult(res.data);
    }, [message, context, testAgent]);

    const handleClear = useCallback(() => { setResult(null); setMessage(""); setContext(""); }, []);

    return (
        <div className="space-y-4">
            <div>
                <h2 className="text-lg font-semibold">{t("admin.support.playground", "Agent Playground")}</h2>
                <p className="text-sm text-muted-foreground">{t("admin.support.playgroundDesc", "Test the support agent with a simulated customer message and inspect tool calls")}</p>
            </div>
            <div className="space-y-3">
                <div>
                    <label className="text-sm font-medium mb-1 block">{t("admin.support.testMessage", "Customer Message")}</label>
                    <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3}
                        placeholder={t("admin.support.testMessagePlaceholder", "e.g. How do I create a workflow with a scheduled trigger?")} />
                </div>
                <div>
                    <label className="text-sm font-medium mb-1 block">{t("admin.support.extraContext", "Extra Context (optional)")}</label>
                    <Textarea value={context} onChange={(e) => setContext(e.target.value)} rows={2}
                        placeholder={t("admin.support.extraContextPlaceholder", "Additional instructions for the agent...")} className="text-sm" />
                </div>
                <div className="flex gap-2">
                    <Button onClick={handleRun} disabled={isLoading || !message.trim()}>
                        {isLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Play className="w-4 h-4 mr-1" />}
                        {isLoading ? t("admin.support.running", "Running...") : t("admin.support.runTest", "Run Test")}
                    </Button>
                    {result && <Button variant="outline" onClick={handleClear}><Trash2 className="w-4 h-4 mr-1" />{t("common.clear", "Clear")}</Button>}
                </div>
            </div>
            {result && <PlaygroundResult result={result} />}
        </div>
    );
}

function PlaygroundResult({ result }) {
    const { t } = useTranslation();
    return (
        <div className="space-y-4 pt-2 border-t">
            <div>
                <h3 className="text-sm font-semibold mb-2">{t("admin.support.agentReply", "Agent Reply")}</h3>
                <div className="border rounded-lg p-4 bg-background prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: result.reply }} />
            </div>
            {result.verboseTrace?.length > 0 && <PlaygroundTracePanel trace={result.verboseTrace} />}
        </div>
    );
}
